import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FileText, LayoutTemplate, Trash2 } from "lucide-react";
import * as api from "../lib/api";

const PERIOD_LABEL = { week: "Weekly", month: "Monthly", quarter: "Quarterly" };

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function ReportsPage() {
  const [reports, setReports] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.getReports()
      .then(d => setReports(d.reports || []))
      .catch(err => {
        console.error("[Reports] getReports failed:", { status: err.response?.status, message: err.message });
        setError(`Couldn't load reports${err.response?.status ? ` (HTTP ${err.response.status})` : ""}.`);
      });
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this report? This can't be undone.")) return;
    await api.deleteReport(id);
    setReports(rs => rs.filter(r => r.id !== id));
  };

  const visible = (reports || []).filter(r => filter === "all" || r.period === filter);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <FileText size={20} strokeWidth={2} className="text-zinc-900" />
          <div>
            <h1 className="text-xl font-bold text-zinc-900">Reports</h1>
            <p className="text-xs text-zinc-500 mt-0.5">HTML reports your agents saved to this org.</p>
          </div>
        </div>
        <Link
          to="/dashboard/reports/templates"
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold border border-zinc-100 bg-white text-zinc-900 hover:bg-zinc-50 rounded-lg"
        >
          <LayoutTemplate size={14} strokeWidth={2} />
          Templates
        </Link>
      </div>

      {reports && reports.length > 0 && (
        <div className="flex gap-1 mb-4">
          {["all", "week", "month", "quarter"].map(f => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs rounded-md border ${
                filter === f ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-100 hover:bg-zinc-50"
              }`}
            >
              {f === "all" ? "All" : PERIOD_LABEL[f]}
            </button>
          ))}
        </div>
      )}

      {error && <p className="text-sm text-red-500">{error}</p>}
      {!reports && !error && <p className="text-sm text-zinc-400">Loading…</p>}

      {reports && reports.length === 0 && (
        <div className="border border-dashed border-zinc-200 rounded-xl py-10 px-6 flex flex-col items-center text-center">
          <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center mb-3">
            <FileText size={18} strokeWidth={2} className="text-zinc-600" />
          </div>
          <p className="text-sm font-bold text-zinc-900">No reports yet</p>
          <p className="text-xs text-zinc-500 mt-1 max-w-sm">
            Ask your agent to run <span className="font-mono text-zinc-900">whyrank-report</span> (e.g. "monthly report") and it will appear here.{" "}
            <Link to="/dashboard/agents" className="underline underline-offset-4 decoration-zinc-300 hover:text-zinc-900">
              Set up your agent
            </Link>
          </p>
        </div>
      )}

      {visible.length > 0 && (
        <div className="bg-white rounded-xl border border-zinc-100 shadow-sm divide-y divide-zinc-100">
          {visible.map(r => (
            <div key={r.id} className="flex items-start gap-3 px-5 py-4 group">
              <Link to={`/dashboard/reports/${r.id}`} className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-zinc-900 truncate group-hover:underline underline-offset-4">{r.title}</p>
                  {r.period && (
                    <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">
                      {PERIOD_LABEL[r.period]}
                    </span>
                  )}
                </div>
                {r.summary && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{r.summary}</p>}
                <p className="text-[10px] text-zinc-400 mt-1.5">
                  {formatDate(r.created_at)}
                  {r.period_start && r.period_end && <> · covers {r.period_start} → {r.period_end}</>}
                  {r.source && <> · {r.source}</>}
                </p>
              </Link>
              <button
                type="button"
                onClick={() => handleDelete(r.id)}
                className="text-zinc-300 hover:text-red-500 p-1"
                title="Delete report"
              >
                <Trash2 size={14} strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>
      )}
      {reports && reports.length > 0 && visible.length === 0 && (
        <p className="text-xs text-zinc-400">No {PERIOD_LABEL[filter].toLowerCase()} reports yet.</p>
      )}
    </div>
  );
}
